import React, { PropsWithChildren } from "react";
import { Collapse } from "@mui/material";
import { cn } from "@helpers/utils";
import { ChevronDown } from "lucide-react";
import BaseHelp from "@reusables/BaseHelp";

type SettingsSectionProps = PropsWithChildren<{
    label: string;

    /**
     * Explanation of what the section's action does, shown in a tooltip next to the label.
     */
    tip?: string;

    /**
     * Renders the header as a toggle, collapsing the section's body. Sections are open by default.
     */
    collapsible?: boolean;

    /**
     * Section-level CTA, placed into a footer strip. Use it when a modal holds several sections,
     * each applying its own request, so it stays clear which part of the form a button submits.
     */
    action?: React.ReactNode;
}>;

export default function SettingsSection({
                                            label,
                                            tip,
                                            collapsible,
                                            action,
                                            children
                                        }: SettingsSectionProps) {
    const [isOpen, setIsOpen] = React.useState(true);

    return (
        <div
            className={cn(
                "flex flex-col border-[1px] border-solid rounded-lg transition-all overflow-hidden relative border-inputs-border-default"
            )}
        >
            <div
                className={cn(
                    "flex flex-row items-center justify-between space-x-2 p-[16px] text-accent user-select-none",
                    collapsible && "cursor-pointer"
                )}
                onClick={collapsible ? () => setIsOpen(!isOpen) : undefined}
            >
                <div className={"flex items-center gap-2"}>
                    <div className={"font-semibold"}>{label}</div>
                    {!!tip && <BaseHelp value={tip} />}
                </div>
                {collapsible && (
                    <ChevronDown
                        className={cn("transition shrink-0")}
                        style={{
                            transform: isOpen ? "rotate(180deg)" : undefined
                        }}
                        size={20}
                    />
                )}
            </div>
            <Collapse in={collapsible ? isOpen : true}>
                <div className={"border-t-[1px] border-t-inputs-border-default space-y-4 p-[16px]"}
                     style={{ borderTopWidth: "1px", borderTopStyle: "solid" }}>
                    {children}
                </div>
                {!!action && (
                    <div className={"border-t-[1px] border-t-inputs-border-default p-[16px] flex justify-end"}
                         style={{ borderTopWidth: "1px", borderTopStyle: "solid" }}>
                        {action}
                    </div>
                )}
            </Collapse>
        </div>
    );
}
